import type { EstimatorInput } from "../../types/index.js";
import { calculateBathroom } from "./bathroom.js";
import { applyItem, type AppliedLineItem, type ProjectCalculationOutput, type ProjectCalculatorFn } from "./shared.js";

/**
 * The Ensuite Estimator (Phase 7). Deliberately NOT a fork of
 * bathroom.ts, and not the bare `ensuite: calculateBathroom` alias this
 * used to be (see rules/index.ts's git history) - Phase 7's brief is
 * explicit that an ensuite must not simply be a renamed bathroom.
 *
 * Instead, this calls `calculateBathroom` for everything that genuinely
 * is shared scope between a bathroom and an ensuite (tub/shower, vanity,
 * toilet, tile, glass, flooring, plumbing, electrical, heated floor,
 * ventilation, custom cabinetry, structural, demolition, accessibility,
 * permit/design - the entire bathroom cost model), then additively
 * layers on the two cost categories that genuinely have no bathroom
 * equivalent: a freestanding tub (an additive fixture, not a
 * replacement for the shower bathroom already prices) and layout
 * complexity (a compartmentalized water closet or dual-walkway layout -
 * a category a standard single-fixture-path bathroom has no need to
 * represent). This satisfies the brief's "reuse the existing Bathroom
 * Estimator architecture," "do not duplicate logic unnecessarily," and
 * "shared logic remains reusable" requirements while still letting "the
 * cost model materially differ" through genuinely new line items.
 *
 * Double vanity, premium/luxury finishes, and custom cabinetry - the
 * brief's other listed differentiators - are already fully represented
 * by bathroom's own `bathroom_vanity` (double/semi-custom/custom
 * options), `finish_level` (premium/luxury multiplier tiers), and
 * `bathroom_custom_cabinetry` questions, which this function inherits
 * for free by delegating to calculateBathroom - no duplicate pricing
 * logic is needed for those.
 */

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

const FREESTANDING_TUB_ITEM_BY_TIER: Record<string, string | null> = {
  none: null,
  standard: "ensuite_freestanding_tub_standard",
  premium: "ensuite_freestanding_tub_premium"
};
const FREESTANDING_TUB_OPTIONS = new Set(["none", "standard", "premium"]);

const LAYOUT_ITEM_BY_TIER: Record<string, string | null> = {
  simple: null,
  compartmentalized: "ensuite_layout_compartmentalized",
  dual_walkway_complex: "ensuite_layout_dual_walkway_complex"
};
const LAYOUT_OPTIONS = new Set(["simple", "compartmentalized", "dual_walkway_complex"]);

export const calculateEnsuite: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const bathroomResult = calculateBathroom(input, catalog, regionalMultiplier);

  const applied: AppliedLineItem[] = [...bathroomResult.applied];
  const assumptions = [...bathroomResult.assumptions];
  const exclusions = [...bathroomResult.exclusions];
  const warnings = [...(bathroomResult.warnings ?? [])];

  const finishLevel = input.finishLevel;

  // --- Freestanding tub (ensuite-only, additive alongside the shower) ---
  const tubTier = rawString(input, "ensuite_freestanding_tub");
  const tubRecognized = tubTier === undefined || FREESTANDING_TUB_OPTIONS.has(tubTier);
  if (tubTier === "none" || tubTier === undefined) {
    exclusions.push({
      code: "excludes_ensuite_freestanding_tub",
      message: "No freestanding tub was selected - no freestanding tub cost is included."
    });
  } else if (!tubRecognized) {
    warnings.push({
      code: "ensuite_freestanding_tub_unrecognized_selection",
      message: `Freestanding tub selection "${tubTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({
      code: "excludes_ensuite_freestanding_tub",
      message: `Freestanding tub selection "${tubTier}" could not be matched to a pricing option - no freestanding tub cost is included in this estimate.`
    });
  } else {
    const tubItemId = FREESTANDING_TUB_ITEM_BY_TIER[tubTier];
    const tub = tubItemId ? applyItem(catalog, tubItemId, { finishLevel, regionalMultiplier }) : null;
    if (tub) {
      applied.push(tub);
      // Audit fix (Phase 7 audit): a freestanding tub is meant to be
      // additive alongside a shower, not alongside another full built-in
      // tub - bathroom.ts has no equivalent for this since it has no
      // concept of a freestanding tub at all, so this check has to live
      // here. "tub" existing configuration kept as-is or replaced
      // like-for-like both still result in a built-in tub being present;
      // any other shower scope replaces that tub with a real shower, so
      // there's no redundancy in that case.
      const existingConfig = rawString(input, "bathroom_existing_configuration");
      const showerScope = rawString(input, "bathroom_shower_scope");
      const resultsInBuiltInTub = existingConfig === "tub" && (showerScope === undefined || showerScope === "keep_existing" || showerScope === "replace_like_for_like");
      if (resultsInBuiltInTub) {
        warnings.push({
          code: "ensuite_contradictory_two_tubs",
          message:
            "A freestanding tub was selected alongside a tub-only configuration that's being kept or replaced like-for-like - that typically means two full tubs in the same ensuite, which is unusual. This estimate includes both as selected; please confirm this is intended.",
          severity: "caution"
        });
      }
    }
  }

  // --- Layout complexity (ensuite-only category) ---------------------------
  const layoutTier = rawString(input, "ensuite_layout_complexity");
  const layoutRecognized = layoutTier === undefined || LAYOUT_OPTIONS.has(layoutTier);
  if (layoutTier === "simple" || layoutTier === undefined) {
    // Audit fix (Phase 7 audit): this used to say "was assumed" even when
    // the user explicitly chose "Simple" - only the unanswered case is
    // genuinely an assumption; an explicit selection is a confirmed
    // answer, not a guess, and should read that way (consistent with how
    // every other category in bathroom.ts's pattern treats an explicit
    // "keep existing"/"no change" selection versus a defaulted one).
    exclusions.push({
      code: "excludes_ensuite_layout_complexity",
      message:
        layoutTier === "simple"
          ? "A simple, single-fixture-path layout was selected - no additional layout complexity cost is included."
          : "A simple, single-fixture-path layout was assumed - no additional layout complexity cost is included."
    });
    if (layoutTier === undefined) {
      assumptions.push({
        code: "ensuite_layout_complexity_defaulted",
        message: 'No layout complexity was specified - defaulted to "simple" for planning purposes.'
      });
    }
  } else if (!layoutRecognized) {
    warnings.push({
      code: "ensuite_layout_complexity_unrecognized_selection",
      message: `Layout complexity "${layoutTier}" was not recognized and could not be priced - this category was excluded from the estimate rather than guessed at.`,
      severity: "caution"
    });
    exclusions.push({
      code: "excludes_ensuite_layout_complexity",
      message: `Layout complexity "${layoutTier}" could not be matched to a pricing option - no additional layout complexity cost is included in this estimate.`
    });
  } else {
    const layoutItemId = LAYOUT_ITEM_BY_TIER[layoutTier];
    const layout = layoutItemId ? applyItem(catalog, layoutItemId, { finishLevel, regionalMultiplier }) : null;
    if (layout) applied.push(layout);
  }

  const output: ProjectCalculationOutput = {
    applied,
    assumptions,
    exclusions,
    anchorMissing: bathroomResult.anchorMissing,
    warnings
  };
  return output;
};
